package com.eladfinish.bible.contest.screens.pastQuizzes

import androidx.compose.runtime.Composable
import com.eladfinish.bible.contest.ui.components.WebViewBridge

/*
 * ראש בראש: a duel between two friends, played in the exam page (`#/exam/duel`, the district-quiz
 * repository's src/views/duel and docs/head-to-head.md). The game is the page's; the app adds:
 *
 *   - links:  https://biblecontest.web.app/duel/{code} opens the room in this screen (and so does
 *             the older biblecontest-f3b7c.web.app/duel/{code}, the same pages on a second host)
 *             (DeepLinks.kt; without the app the landing site's duel.html sends the link to the page)
 *   - share:  the page's "send the code" opens the system share sheet ([rememberExamShareBridge])
 *   - ads:    no banner while a room is open ([isDuelRoomUrl])
 *
 * Remote Config `disable_past_quizzes_duel` switches all three off: the page then falls back
 * to its WhatsApp link, and a duel link opens the room in the browser.
 */

/** A room code as the page makes them (src/duel/room.ts): six of A–Z and 2–9, without I, O, 0 and 1. */
private val DUEL_CODE = Regex("[A-HJ-NP-Z2-9]{6}")

/** [this] as a room code (any case, surrounding spaces ignored), or null when it is not one. */
fun String.toDuelCodeOrNull(): String? = trim().uppercase().takeIf { DUEL_CODE.matches(it) }

/** The page's room screens (`#/exam/duel/{code}`: the lobby, the game and its result), not the duel's home page. */
private val DUEL_ROOM_ROUTE = Regex("/exam/duel/[A-HJ-NP-Z2-9]{6}/?")

/** Whether the page at [url] shows a ראש בראש room (the route is in the fragment; its query is ignored). */
fun isDuelRoomUrl(url: String?): Boolean {
    val route = url?.substringAfter('#', missingDelimiterValue = "")?.substringBefore('?') ?: return false
    return DUEL_ROOM_ROUTE.matches(route)
}

/**
 * The page's invitation goes to the system share sheet through `window.BibleShare.share(text)`.
 * The contract is the page's (src/views/duel/DuelRoute.tsx): without `window.BibleShare` it offers
 * a WhatsApp link instead. Null where there is no WebView (desktop, web).
 */
@Composable
expect fun rememberExamShareBridge(): WebViewBridge?

/** The name the page looks for on `window`. */
internal const val EXAM_SHARE_JS_NAME = "BibleShare"

/** The longest text the page may hand the share sheet; an invitation is a few lines. */
internal const val EXAM_SHARE_MAX_LENGTH = 2_000

/** [text] as the share sheet gets it, or null when there is nothing to share. */
internal fun examShareText(text: String?): String? = text?.trim()?.take(EXAM_SHARE_MAX_LENGTH)?.takeIf { it.isNotEmpty() }
